<template>
  <header
    class="master-top-bar"
    :class="{ 'master-top-bar--search-active': showSearchResults }"
    :style="brandingVars"
    v-if="!isLoading">
    <div
      class="master-top-bar__logo"
      @click="handleLogoClick"
      @keydown.enter="handleLogoClick"
      tabindex="0"
      role="button"
    >
      <VcImage v-if="logo" :image="logo" :alt="logoAlt" />
    </div>

    <!-- Navigation -->
    <div class="master-top-bar__navigation">
      <TopBarNavigation
        :items="topLevelItems"
        :active-item-uid="activeTopBarItemUid"
        @item-click="navItemClick"/>
    </div>

    <!-- Actions (from VcWideTopMenuBar pattern) -->
    <div class="master-top-bar__actions">
      <slot name="actions">
        <!-- Default actions slot - parent can override -->
      </slot>
    </div>
  </header>

  <div v-else class="master-top-bar master-top-bar--loading" :style="brandingVars">
    <VcSkeleton type="text" width="120px" height="32px" />
    <VcSkeleton type="text" width="300px" height="32px" />
    <VcSkeleton type="text" width="200px" height="32px" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import handleNavigation from '@/utils/navigationUtils';
import outgoingPostMessageUtils from '@/utils/eventCommunicationUtils/outgoingPostMessageUtils';
import { hasAction, executeAction } from '../utils/actionExecutor';
import TopBarNavigation from './TopBarNavigation.vue';

export default {
  name: 'MasterTopBar',
  components: {
    TopBarNavigation,
  },
  props: {
    logoAlt: {
      type: String,
      default: 'Logo',
    },
    defaultRoute: {
      type: String,
      default: '/dashboard',
    },
  },
  computed: {
    ...mapGetters('AuthStore', ['staffUid']),
    ...mapGetters('SearchBarStore', ['showSearchResults', 'isWaitingForSearchResponse']),
    ...mapGetters('AngularStore', ['isAngularLoaded']),
    ...mapGetters('PlatformNavigationStore', [
      'topLevelItems',
      'isLoading',
      'hasFatalError',
      'rootItem',
      'activeTopBarItemUid',
      'navigationTree',
      'showSidebar',
    ]),
    ...mapGetters('BusinessStore', [
      'logo',
      'primaryColor',
      'attributes',
      'directorySettings',
      'isPartnerDirectory',
      'hasHelpLinks',
      'businessId',
    ]),
    brandingVars() {
      return {
        '--primary-color': this.primaryColor,
      };
    },
  },
  watch: {
    // Watch route changes to catch navigation that doesn't go through beforeEach
    // This handles cases like businessPlatformSDK.navigate() and other programmatic navigation
    $route: {
      handler(to) {
        if (this.navigationTree && this.navigationTree.length > 0) {
          this.setActiveItemFromRoute({ path: to.path });
        }
      },
      immediate: true,
      deep: true,
    },
    // Re-check active item when navigation tree loads
    navigationTree: {
      handler(tree) {
        if (tree && tree.length > 0) {
          this.setActiveItemFromRoute({ path: this.$route.path });
        }
      },
      immediate: true,
    },
    hasFatalError(hasError) {
      if (hasError) {
        this.handleFatalError();
      }
    },
    isAngularLoaded(loaded) {
      if (loaded) {
        this.replaySidebarState();
      }
    },
    showSearchResults(value) {
      this.setIsOverlayDisplay(value);
    },
  },
  async created() {
    // Register router beforeEach guard to handle all route changes
    // This ensures menu state is updated for deep links, redirects, and programmatic navigation
    this.$router.beforeEach(this.routeChange);
    
    try {
      await this.fetchNavigation();
    } catch (error) {
      // Error is already handled by store
      console.error('Failed to load navigation:', error);
    }
  },
  beforeDestroy() {
    // Note: Vue Router doesn't provide a direct way to remove beforeEach guards
    // The guard will be automatically cleaned up when the component is destroyed
  },
  methods: {
    ...mapActions('PlatformNavigationStore', {
      storeHandleItemClick: 'handleItemClick',
      fetchNavigation: 'fetchNavigation',
      setActiveItemFromRoute: 'setActiveItemFromRoute',
    }),
    handleLogoClick() {
      // If rootItem exists, treat logo click like clicking the root navigation item
      // This will navigate to first child (dashboard) if rootItem has children,
      // or to rootItem.url if it doesn't, and properly handle POV/Angular routing
      if (this.rootItem) {
        this.executeItemClick(this.rootItem);
      } else {
        // Fallback: navigate to default route if rootItem not available
        handleNavigation({ path: this.defaultRoute });
      }
    },
    navItemClick(item) {
      this.executeItemClick(item);
    },
    executeItemClick(item) {
      this.storeHandleItemClick({ item });
      if (hasAction(item)) {
        executeAction(item.action);
      }
    },
    handleFatalError() {
      // Redirect to error page or show error state
      this.$router.push('/error');
    },
    replaySidebarState() {
      outgoingPostMessageUtils.post('toggle-sidebar-visibility', {
        showSidebar: this.showSidebar,
      });
    },
    routeChange(to, from, next) {
      if (!this.navigationTree || this.navigationTree.length === 0) {
        next();
        return;
      }

      // Determine the current path
      // Use to.path if available (from router guard), otherwise use current route
      // Strip query params for matching (query params are handled by the page, not navigation)
      const currentPath = to?.path || this.$route.path;
      
      // Update active item based on route
      this.setActiveItemFromRoute({ path: currentPath });
      
      // Call next() to continue navigation
      next();
    },
  },
};
</script>

<style lang="scss" scoped>
.master-top-bar {
  display: flex;
  align-items: center;
  gap: var(--size-value4);
  padding-inline: var(--size-value4);
  background-color: var(--primary-color, var(--white));
  color: var(--v-text-base);
  border-bottom: 1px solid var(--neutral-lighten-1);
  min-height: 56px;

  &--loading {
    justify-content: space-between;
  }

  &__logo {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    cursor: pointer;
    height: 40px; // Fixed height for consistent top bar

    &:focus {
      outline: 2px solid var(--primary-color);
      outline-offset: 2px;
    }

    // Style the VcImage component - height fills container, width auto-adjusts
    ::v-deep .VcImage {
      height: 100%;

      .image {
        height: 100%;
        width: auto;
        object-fit: contain;
      }
    }
  }

  &__navigation {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
  }

  &__actions {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: var(--size-value2);
  }
}
</style>
